import { useCallback, useEffect, useRef, useState } from 'react'
import { currentUser } from '@/lib/api'

export interface Notification {
  id: string
  title: string
  message: string
  time: string
  read: boolean
}

interface FailedPayload {
  url: string
  description: string
  status: number
  time: string
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const esRef = useRef<EventSource | null>(null)

  const addNotification = useCallback((n: Omit<Notification, 'id' | 'read'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    setNotifications((prev) => [{ ...n, id, read: false }, ...prev].slice(0, 50))
  }, [])

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }, [])

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  useEffect(() => {
    const user = currentUser.get()
    if (user?.role_name !== 'ROLE_SUPPORT') return

    const url = `${BASE_URL}/api/v1/events`
    const es = new EventSource(url, { withCredentials: true })
    esRef.current = es

    es.addEventListener('request_failed', (e) => {
      try {
        const payload = JSON.parse(e.data) as FailedPayload
        addNotification({
          title: 'Request failed',
          message: payload.description || payload.url,
          time: payload.time,
        })
      } catch {
        // ignore malformed events
      }
    })

    es.onerror = () => {
      // EventSource auto-retries on network drop; close on 401 to avoid a retry loop
      if (es.readyState === EventSource.CLOSED) {
        esRef.current = null
      }
    }

    return () => {
      es.close()
      esRef.current = null
    }
  }, [addNotification])

  const unreadCount = notifications.filter((n) => !n.read).length

  return { notifications, unreadCount, markAllRead, dismiss }
}
